import { message } from 'antd'
import {
  applyMeasurementSystemToFormField,
  assetOverviewPermissionsCheck,
  createEditedObject,
  createUniqueFormFields,
} from 'components/form/formHelpers'
import {
  HandleSaveToRolodexType,
  SimpleFormField,
} from 'components/form/formTypes'
import { GeckoToggleForm } from 'components/form/GeckoToggleForm'
import { AssetDataObjType } from 'features/assetDatabase/types/assetOverviewTypes'
import { CustomField } from 'features/assetDatabase/types/customField'
import { getJSONFromAssetModelFormFields } from 'features/assetDatabase/utils/assetDBAssetsModels'
import { createUpdatedUnitObject } from 'features/assetDatabase/utils/assetDBHelpers'
import React, { forwardRef } from 'react'
import { SubmitHandler } from 'react-hook-form'
import {
  GeckoAssetModel,
  GeckoMeasurementSystem,
  GeckoUnit,
  regenerateAssetModel,
} from 'types'

interface AssetFormProps {
  assetDataObj: AssetDataObjType
  measurementSystem: GeckoMeasurementSystem
  inEditMode: boolean
  inResolveMode: boolean
  userPermissions: ReadonlySet<string>
  forceRefresh: () => Promise<void>
  handleSaveToRolodex: HandleSaveToRolodexType
  unit: GeckoUnit
  customFields: CustomField[]
}

export const AssetForm = forwardRef<HTMLFormElement, AssetFormProps>(
  (
    {
      measurementSystem,
      assetDataObj,
      unit,
      customFields,
      inEditMode,
      inResolveMode,
      userPermissions,
      forceRefresh,
      handleSaveToRolodex,
    },
    ref
  ) => {
    const [messageApi, contextHolder] = message.useMessage()

    const assetFormOnSubmit: SubmitHandler<SimpleFormField> = (data) => {
      const rolodexPatch = []
      const convertedFormFields = createEditedObject(
        data,
        createUniqueFormFields([assetDataObj.assetFormFields])
      )

      if (assetDataObj.assetModel) {
        const editedAssetModelFields = getJSONFromAssetModelFormFields(
          assetDataObj.assetModel.asJSON(),
          convertedFormFields[0].formFields
        )

        const assetModelRaw = assetDataObj.assetModel.raw
        assetModelRaw.data = editedAssetModelFields

        const editedAssetModel = regenerateAssetModel(
          GeckoAssetModel.CreateFromJSON(assetModelRaw)
        )

        rolodexPatch.push(editedAssetModel)
      }
      rolodexPatch.push(
        createUpdatedUnitObject(
          unit,
          convertedFormFields[0].formFields,
          customFields
        )
      )
      handleSaveToRolodex(rolodexPatch, messageApi, forceRefresh, 'asset')
    }

    return (
      <>
        {contextHolder}
        <GeckoToggleForm
          formFieldsByUniqueID={[assetDataObj.assetFormFields]}
          inEditMode={inEditMode}
          inResolveMode={inResolveMode}
          permissionsCheck={{
            userPermissions: userPermissions,
            permissionsChecker: assetOverviewPermissionsCheck,
          }}
          measurementApplication={{
            measurementApplication: applyMeasurementSystemToFormField,
            measurementSystem: measurementSystem,
          }}
          onSubmit={assetFormOnSubmit}
          ref={ref}
        />
      </>
    )
  }
)

AssetForm.displayName = 'AssetForm'
